import React from 'react';
import { motion } from 'framer-motion';
import { Award, Globe, Megaphone, Calendar, Heart } from 'lucide-react';

const TrustSignals: React.FC = () => {
  const awards = [
    { name: 'Best Digital Agency 2023', organization: 'Digital Excellence Awards' },
    { name: 'Top Web Development', organization: 'Tech Innovation Awards' },
    { name: 'Luxury Brand Partner', organization: 'Premium Marketing Society' },
  ];

  // Client logos from assets folder
  const clients = [
    { id: 'client-1', name: 'Aamisha Foods', logo: '/clientlogo/Color_Drop_with_Aamisha_Foods.jpg', category: 'Food & Beverage' },
    { id: 'client-2', name: 'Arihant Hospital', logo: '/clientlogo/Color_Drop_with_Arihant_Hospital.jpg', category: 'Healthcare' },
    { id: 'client-3', name: 'Ashoka Hotel', logo: '/clientlogo/Color_Drop_with_Ashoka_Hotel.jpg', category: 'Hospitality' },
    { id: 'client-4', name: 'BookMyAcademe', logo: '/clientlogo/Color_Drop_with_BookMyAcademe.jpg', category: 'Education' },
    { id: 'client-5', name: 'Bunglow Makers', logo: '/clientlogo/Color_Drop_with_Bunglow_Makers.jpg', category: 'Real Estate' },
    { id: 'client-6', name: 'Dr. Abhijeet Bhasin', logo: '/clientlogo/Color_Drop_with_Dr_Abhijeet_Bhasin_Indore.jpg', category: 'Healthcare' },
    { id: 'client-7', name: 'Dr. Bhatia', logo: '/clientlogo/Color_Drop_with_Dr_Bhatia.jpg', category: 'Healthcare' },
    { id: 'client-8', name: 'Geet Dental Clinic', logo: '/clientlogo/Color_Drop_with_Geet_Dental_Clinic.jpg', category: 'Healthcare' },
    { id: 'client-9', name: 'INDWheels', logo: '/clientlogo/Color_Drop_with_INDWheels.jpg', category: 'Automotive' },
    { id: 'client-10', name: 'ISC', logo: '/clientlogo/Color_Drop_with_ISC.jpg', category: 'Education' },
    { id: 'client-11', name: 'Khalsa Pagree', logo: '/clientlogo/Color_Drop_with_Khalsa_Pagree.jpg', category: 'Retail' },
    { id: 'client-12', name: 'Manbhadra Fincorp', logo: '/clientlogo/Color_Drop_with_Manbhadra_Fincorp.jpg', category: 'Finance' },
    { id: 'client-13', name: 'Medab Overseas', logo: '/clientlogo/Color_Drop_with_Medab_Overseas.jpg', category: 'Healthcare' },
    { id: 'client-14', name: 'Millet Aahaar', logo: '/clientlogo/Color_Drop_with_Millet_Aahaar.jpg', category: 'Food & Beverage' },
    { id: 'client-15', name: 'Mushkil Asan Kendra', logo: '/clientlogo/Color_Drop_with_Mushkil_Asan_Kendra.jpg', category: 'Services' },
    { id: 'client-16', name: 'Oink', logo: '/clientlogo/Color_Drop_with_Oink.jpg', category: 'Food & Beverage' },
    { id: 'client-17', name: 'Olive Dental & Physio', logo: '/clientlogo/Color_Drop_with_Olive_Dental&Physio.jpg', category: 'Healthcare' },
    { id: 'client-18', name: 'Palomi School', logo: '/clientlogo/Color_Drop_with_Palomi_School.jpg', category: 'Education' },
    { id: 'client-19', name: 'Peekay Pharma', logo: '/clientlogo/Color_Drop_with_Peekay_Pharma.jpg', category: 'Healthcare' },
    { id: 'client-20', name: 'Peppa Pork', logo: '/clientlogo/Color_Drop_with_Peppa_Pork.jpg', category: 'Food & Beverage' },
    { id: 'client-21', name: 'Saish Caterers', logo: '/clientlogo/Color_Drop_with_Saish_Caterers.jpg', category: 'Food & Beverage' },
    { id: 'client-22', name: 'Sampoorna Sodani Diagnostic', logo: '/clientlogo/Color_Drop_with_Sampoorna_Sodani_Diagnostic.jpg', category: 'Healthcare' },
    { id: 'client-23', name: 'Sapna Sandwich Indore', logo: '/clientlogo/Color_Drop_with_Sapna_Sandwhich_Indore.jpg', category: 'Food & Beverage' },
    { id: 'client-24', name: 'Sigma Classes', logo: '/clientlogo/Color_Drop_with_Sigma_Classes.jpg', category: 'Education' },
    { id: 'client-25', name: 'Spirabiotec', logo: '/clientlogo/Color_Drop_with_Spirabiotec.jpg', category: 'Healthcare' },
    { id: 'client-26', name: 'Storm Overseas', logo: '/clientlogo/Color_Drop_with_Storm_Overseas.jpg', category: 'Healthcare' },
    { id: 'client-27', name: 'VDC', logo: '/clientlogo/Color_Drop_with_VDC.jpg', category: 'Technology' },
    { id: 'client-28', name: 'Veerjees', logo: '/clientlogo/Color_Drop_with_Veerjees.jpg', category: 'Retail' },
    { id: 'client-29', name: 'Vostok Herbals', logo: '/clientlogo/Color_Drop_with_Vostok_Herbals.jpg', category: 'Healthcare' },
    { id: 'client-30', name: 'Wilcure', logo: '/clientlogo/Color_Drop_with_Wilcure.jpg', category: 'Healthcare' },
  ];

  // Duplicate clients for infinite scroll effect
  const duplicatedClients = [...clients, ...clients];

  const stats = [
    { icon: Megaphone, value: '1365+', label: 'Ad Campaigns Done' },
    { icon: Globe, value: '18+', label: 'Location Served' },
    { icon: Calendar, value: '12+', label: 'Years Experience' },
    { icon: Heart, value: '560', label: 'Satisfied Clients' },
  ];

  return (
    <section className="py-16 bg-white">
      <div className="container-custom">
        {/* Awards Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: false }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl font-serif font-bold mb-4">
            Recognized Excellence
          </h2>
          <p className="text-neutral-600 max-w-2xl mx-auto mb-12">
            Our commitment to quality and innovation has earned us recognition from industry leaders and prestigious organizations.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {awards.map((award, index) => (
              <motion.div
                key={award.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: false }}
                className="bg-gradient-to-br from-primary-50 to-accent-50 p-6 rounded-xl border border-primary-100"
              >
                <Award className="w-12 h-12 text-primary-600 mx-auto mb-4" />
                <h3 className="font-semibold text-lg mb-2">{award.name}</h3>
                <p className="text-neutral-600 text-sm">{award.organization}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: false }}
          className="bg-gradient-to-r from-primary-500 to-accent-500 rounded-2xl p-8 md:p-12 mb-16"
        >
          <div className="text-center mb-8">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-white mb-4">
              Trusted by Industry Leaders
            </h2>
            <p className="text-white/90 max-w-2xl mx-auto">
              Our proven track record speaks for itself. We've helped hundreds of luxury brands achieve their digital goals.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: false }}
                className="text-center"
              >
                <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="w-8 h-8 text-white" />
                </div>
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-white/90 text-sm">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Partners Section */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: false }}
          className="text-center"
        >
          <h2 className="text-2xl md:text-3xl font-serif font-bold mb-4">
            Our Esteemed Clients
          </h2>
          <p className="text-neutral-600 mb-8">
            We're proud to work with some of the world's most prestigious brands.
          </p>
          <div className="relative">
            <div className="flex gap-8 infinite-scroll-container">
              {duplicatedClients.map((client, index) => (
                <div
                  key={`carousel-${index}`}
                  className="flex-shrink-0 w-40 h-40 bg-white rounded-xl p-6 border border-neutral-200 shadow-sm hover:shadow-lg hover:border-primary-600 transition-all duration-300 flex items-center justify-center aspect-square cursor-pointer"
                >
                  <img
                    src={client.logo}
                    alt={client.name}
                    className="w-full h-full object-contain transition-all duration-300 opacity-70 hover:opacity-100"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.style.display = 'none';
                      const parent = target.parentElement;
                      if (parent) {
                        parent.innerHTML = `<div class="text-center"><div class="text-xl font-bold text-primary-600">${client.name}</div></div>`;
                      }
                    }}
                  />
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TrustSignals;
